/**
 * Service Catalog Hook
 *
 * The full list of orderable services, used to render the per-provider
 * "Manage Services" checklist.
 *
 * Backend: GraphQL serviceTemplates(isActive: true, limit: Int)
 *
 * Caveat: the resolver additionally filters on the effective/expiry window when
 * isActive is true, while the PUT write path validates is_active only. The catalog
 * is therefore a subset of what the backend will accept — a provider can hold a
 * service that no longer appears here, which the dialog handles explicitly.
 */

import { useQuery } from '@tanstack/react-query'
import { getServiceTemplates } from '@/services/orderApi'
import type { ServiceCatalogItem } from '@/types/providerServices'

/** Generous enough for the current catalog; client-side search filters it. */
const CATALOG_LIMIT = 200

export function useServiceCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ['serviceCatalog'],
    queryFn: () => getServiceTemplates({ isActive: true, limit: CATALOG_LIMIT }),
    staleTime: 1000 * 60 * 60, // 1 hour — the catalog rarely changes
    enabled,
  })

  return {
    catalog: (query.data ?? []) as ServiceCatalogItem[],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
